import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import Icon from './ui/Icon'
import AddressEditor from './AddressEditor'

/**
 * The address editor in a dialog, opened from the pencil beside the address.
 *
 * A dialog rather than an inline form because the Bangladesh case needs four
 * stacked selects, which would push the rest of the profile card around every
 * time someone opened it.
 */
const AddressModal = ({ isOpen, user, onClose, onSaved }) => {
  const { t } = useTranslation()

  // Escape closes it. Nothing is saved until the button is pressed, so leaving
  // this way can never lose committed work.
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="address-modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      {/* A column, so the heading stays put and only the fields scroll — on a
          short screen the member could otherwise scroll the title away and
          lose track of what the dialog was for. Wider on a desktop, where
          max-w-sm left the four selects in a needlessly cramped strip. */}
      <div className="bg-card border border-border rounded-xl shadow-strong
                      w-full max-w-sm sm:max-w-md lg:max-w-lg
                      max-h-[85vh] flex flex-col">
        <div className="flex justify-between items-start gap-2 px-5 sm:px-6 pt-5 sm:pt-6 pb-3 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Icon name="MapPin" className="w-5 h-5 text-primary shrink-0" />
            <h2 id="address-modal-title" className="text-lg font-bold text-foreground truncate">
              {t('address.title')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            // Not address.cancel: the editor's own Cancel button already uses
            // that, and a dialog with two buttons announced "Cancel" gives a
            // screen-reader user no way to tell them apart.
            aria-label={t('common.close')}
            className="text-muted-foreground hover:text-foreground text-2xl leading-none shrink-0 -mt-1
                       focus:outline-none focus:ring-2 focus:ring-border rounded"
          >
            ×
          </button>
        </div>

        <div className="px-5 sm:px-6 pb-5 sm:pb-6 overflow-y-auto">
          <AddressEditor user={user} onSaved={onSaved} onCancel={onClose} />
        </div>
      </div>
    </div>,
    document.body
  )
}

export default AddressModal
